import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import {
  Button,
  ButtonGroup,
  Collapse,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { showModalTop } from '../../actions';

const ImageEditorAIButton = ({
  action,
  perform,
  callbackAIAction,
  collapseOpen,
  handleCollapseOpen,
  cropperData,
  openSelectFileModal,
  cropDimensions,
}) => {
  const intl = useIntl();

  const [blurBackgroundDropdownIsOpen, setBlurBackgroundDropdownIsOpen] = useState(false);
  const [blurBackgroundAmount, setBlurBackgroundAmount] = useState(3);
  const [replaceBackgroundPrompt, setReplaceBackgroundPrompt] = useState('');
  const [replaceBackgroundTab, setReplaceBackgroundTab] = useState('prompt');
  const [increaseResolutionDropdownIsOpen, setIncreaseResolutionDropdownIsOpen] = useState(false);
  const [increaseResolutionAmount, setIncreaseResolutionAmount] = useState(2);
  const [replaceBackgroundImage, setReplaceBackgroundImage] = useState(null);

  const blurAmounts = [
    {
      value: 1,
      label: intl.formatMessage({ defaultMessage: 'Light blur' }),
    },
    {
      value: 3,
      label: intl.formatMessage({ defaultMessage: 'Medium blur' }),
    },
    {
      value: 5,
      label: intl.formatMessage({ defaultMessage: 'Strong blur' }),
    },
  ];

  const increaseResolutionLimit = { width: 8192, height: 8192 };

  const getIncreaseResolutionAmounts = () => {
    if (!cropperData) {
      return [];
    }

    const { imageData } = cropperData;
    if (!imageData || !imageData.naturalWidth || !cropperData.imageData.naturalHeight) {
      return [];
    }

    const { naturalWidth, naturalHeight } = imageData;
    const { width, height } = increaseResolutionLimit;

    const amounts = [];

    if (naturalWidth * 2 <= width && naturalHeight * 2 <= height) {
      amounts.push({
        value: 2,
        label: 'X2',
      });
    }

    if (naturalWidth * 4 <= width && naturalHeight * 4 <= height) {
      amounts.push({
        value: 4,
        label: 'X4',
      });
    }

    return amounts;
  };

  const handleOnClick = () => {
    if (action.name === 'blur-background') {
      handleCollapseOpen(collapseOpen === 'blur-background' ? null : 'blur-background');
      callbackAIAction(action.name, 'main', {});
    } else if (action.name === 'replace-background') {
      handleCollapseOpen(collapseOpen === 'replace-background' ? null : 'replace-background');
      callbackAIAction(action.name, 'main', {});
    } else if (action.name === 'expand') {
      callbackAIAction(action.name, 'main', { expandingMode: collapseOpen !== 'expand' });
      handleCollapseOpen(collapseOpen === 'expand' ? null : 'expand');
    } else if (action.name === 'increase-resolution') {
      callbackAIAction(action.name, 'main', { expandingMode: collapseOpen !== 'increase-resolution' });
      handleCollapseOpen(collapseOpen === 'increase-resolution' ? null : 'increase-resolution');
    } else {
      callbackAIAction(action.name, 'main', {});
      perform();
    }
  };

  const shouldDisableMainButton = () => {
    if ([
      'blur-background',
      'replace-background',
      'expand',
      'increase-resolution',
    ].includes(action.name)) {
      return false;
    }

    return action.done;
  };

  const isButtonActive = () => {
    if (['blur-background', 'replace-background', 'expand', 'increase-resolution'].includes(action.name)) {
      return collapseOpen === action.name;
    }

    return false;
  };

  const handleSelectFileModal = () => {
    const params = {
      className: 'modal-select-file modal-select-file-enhanced',
      handleSelectFile: () => {},
      handleFileUrl: () => {},
      handleFileData: (file) => {
        setReplaceBackgroundImage(file);
      },
      showRemoveButton: false,
    };

    openSelectFileModal(params);
  };

  const handleExpand = () => {
    const data = callbackAIAction(action.name, 'getData', {});

    perform({
      canvas_size: data.canvas_size,
      original_image_location: data.original_image_location,
      original_image_size: data.original_image_size,
    });
  };

  const canPerformExpand = cropDimensions.x <= 5000 && cropDimensions.y <= 5000;

  const increaseResolutionAmounts = getIncreaseResolutionAmounts();

  if (!action) return null;

  return (
    <>
      <div className="image-editor-controls-ai">
        <Button
          onClick={handleOnClick}
          disabled={shouldDisableMainButton()}
          id={`ImageEditor${action.componentId}Button`}
          size="sm"
          className={classnames('image-editor-ai-button', { 'image-editor-ai-button-active': isButtonActive() })}
        >
          <>
            {action.icon && <i className={`${action.icon} fa-fw mr-2`} />}
            {action.button}
          </>
        </Button>
        {action.done && (
        <UncontrolledTooltip
          target={`ImageEditor${action.componentId}Button`}
        >
          {action.alreadyDoneMessage}
        </UncontrolledTooltip>
        )}
      </div>
      {action.name === 'blur-background' && (
        <Collapse
          isOpen={collapseOpen === action.name}
        >
          <div className="collapse-wrapper">
            <div className="collapse-container">
              <div>
                <Dropdown
                  isOpen={blurBackgroundDropdownIsOpen}
                  toggle={() => setBlurBackgroundDropdownIsOpen(!blurBackgroundDropdownIsOpen)}
                  size="sm"
                >
                  <>
                    <DropdownToggle
                      color="link"
                    >
                      {blurAmounts.find(a => a.value === blurBackgroundAmount)?.label}
                      <i className="far fa-caret-down" />
                    </DropdownToggle>
                    <DropdownMenu>
                      {blurAmounts.map((amount) => (
                        <DropdownItem
                          key={amount.value}
                          onClick={() => setBlurBackgroundAmount(amount.value)}
                        >
                          {amount.label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </>
                </Dropdown>
                <Button
                  onClick={() => perform({ scale: blurBackgroundAmount })}
                  disabled={action.done}
                  id={`ImageEditor${action.componentId}ButtonActionButton`}
                  size="sm"
                  className="image-editor-ai-button"
                  color="primary"
                >
                  {action.actionMessage}
                </Button>
              </div>
            </div>
          </div>
        </Collapse>
      )}
      {action.name === 'replace-background' && (
        <Collapse
          isOpen={collapseOpen === action.name}
        >
          <div className="collapse-wrapper">
            <div className="collapse-container">
              <div>
                <ButtonGroup
                  className="image-editor-controls-ai-tabs"
                >
                  <Button
                    size="sm"
                    onClick={() => setReplaceBackgroundTab('prompt')}
                    className={classnames({ 'image-editor-controls-ai-tab-active': replaceBackgroundTab === 'prompt' })}
                  >
                    {intl.formatMessage({ defaultMessage: 'Use prompt' })}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setReplaceBackgroundTab('image')}
                    className={classnames({ 'image-editor-controls-ai-tab-active': replaceBackgroundTab === 'image' })}
                  >
                    {intl.formatMessage({ defaultMessage: 'Use reference image' })}
                  </Button>
                </ButtonGroup>
              </div>
            </div>
            <div className="collapse-container">
              {replaceBackgroundTab === 'prompt' && (
                <div>
                  <Input
                    value={replaceBackgroundPrompt}
                    onChange={(e) => setReplaceBackgroundPrompt(e.target.value)}
                    placeholder={intl.formatMessage({ defaultMessage: 'Describe your new background' })}
                  />
                </div>
              )}
              {replaceBackgroundTab === 'image' && (
                <div className="image-editor-controls-ai-replace-background-image-wrapper">
                  <Button
                    color="link"
                    size="sm"
                    onClick={handleSelectFileModal}
                  >
                    {intl.formatMessage({ defaultMessage: 'Select image...' })}
                  </Button>
                  <p>
                    {!replaceBackgroundImage && intl.formatMessage({ defaultMessage: 'No image selected' })}
                    {replaceBackgroundImage && replaceBackgroundImage.display_name}
                  </p>
                </div>
              )}
            </div>
            <div className="collapse-container">
              <div>
                <Button
                  onClick={() => perform({
                    ...(replaceBackgroundTab === 'prompt' && { prompt: replaceBackgroundPrompt }),
                    ...(replaceBackgroundTab === 'image' && { reference_image: replaceBackgroundImage }),
                  })}
                  disabled={action.done}
                  id={`ImageEditor${action.componentId}ButtonActionButton`}
                  size="sm"
                  className="image-editor-ai-button"
                  color="primary"
                >
                  {action.actionMessage}
                </Button>
              </div>
            </div>
          </div>
        </Collapse>
      )}
      {action.name === 'expand' && (
        <Collapse
          isOpen={collapseOpen === 'expand'}
        >
          <div className="collapse-wrapper">
            <div className="collapse-container">
              <div>
                <p>{intl.formatMessage(
                  { defaultMessage: 'Drag the edges of your image to expand it. Image size is ({width} x {height}) px.' },
                  { width: cropDimensions.x, height: cropDimensions.y },
                )}
                </p>
              </div>
            </div>
            <div className="collapse-container">
              <div>
                {canPerformExpand &&
                (
                <Button
                  onClick={handleExpand}
                  disabled={action.done}
                  id={`ImageEditor${action.componentId}ButtonActionButton`}
                  size="sm"
                  className="image-editor-ai-button"
                  color="primary"
                >
                  {action.actionMessage}
                </Button>
                )}
                {!canPerformExpand && (
                  <p className="image-editor-ai-button-warning">{intl.formatMessage(
                    { defaultMessage: 'Maximum resolution for expanded image is (5000 x 5000) px.' },
                  )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Collapse>
      )}
      {action.name === 'increase-resolution' && (
        <Collapse
          isOpen={collapseOpen === action.name}
        >
          <div className="collapse-wrapper">
            {increaseResolutionAmounts.length > 0 && (
              <div className="collapse-container">
                <div>
                  <Dropdown
                    isOpen={increaseResolutionDropdownIsOpen}
                    toggle={
                    () => setIncreaseResolutionDropdownIsOpen(!increaseResolutionDropdownIsOpen)
                  }
                    size="sm"
                  >
                    <>
                      <DropdownToggle
                        color="link"
                      >
                        {increaseResolutionAmounts.find(
                          a => a.value === increaseResolutionAmount,
                        )?.label}
                        <i className="far fa-caret-down" />
                      </DropdownToggle>
                      <DropdownMenu>
                        {increaseResolutionAmounts.map((amount) => (
                          <DropdownItem
                            key={amount.value}
                            onClick={() => setIncreaseResolutionAmount(amount.value)}
                          >
                            {amount.label}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </>
                  </Dropdown>
                  <Button
                    onClick={() => perform({ desired_increase: increaseResolutionAmount })}
                    disabled={action.done}
                    id={`ImageEditor${action.componentId}ButtonActionButton`}
                    size="sm"
                    className="image-editor-ai-button"
                    color="primary"
                  >
                    {action.actionMessage}
                  </Button>
                </div>
              </div>
            )}
            {(increaseResolutionAmounts.length === 0 && cropperData) && (
              <div className="collapse-container">
                <div>
                  <p className="my-1 image-editor-ai-button-warning">
                    {intl.formatMessage(
                      { defaultMessage: 'Maximum image size after increase is ({limitWidth} x {limitHeight}) px, this operation is not supported for this image ({imageWidth} x {imageHeight}) px.' },
                      {
                        limitWidth: increaseResolutionLimit.width,
                        limitHeight: increaseResolutionLimit.height,
                        imageWidth: cropperData.imageData.naturalWidth,
                        imageHeight: cropperData.imageData.naturalHeight,
                      },
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </Collapse>
      )}
    </>
  );
};

const mapStateToProps = state => ({
  clientSettings: state.App.settings,
});

const mapDispatchToProps = dispatch => ({
  openSelectFileModal: (params) => {
    dispatch(showModalTop('FileSelectModalEnhanced', params));
  },
});

ImageEditorAIButton.propTypes = {
  action: PropTypes.object.isRequired,
  perform: PropTypes.func.isRequired,
  callbackAIAction: PropTypes.func,
  cropperData: PropTypes.object,
  handleCollapseOpen: PropTypes.func.isRequired,
  openSelectFileModal: PropTypes.func.isRequired,
  collapseOpen: PropTypes.string,
  cropDimensions: PropTypes.object.isRequired,
};

ImageEditorAIButton.defaultProps = {
  collapseOpen: null,
  cropperData: null,
  callbackAIAction: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(ImageEditorAIButton);
